import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import AppShell from "../components/AppShell";
import MobileHeader from "../components/MobileHeader";
import { getStoredTheme, setStoredTheme } from "../../../../theme/themeStorage";
import "./settings.css";

// Real app-level Settings — distinct from the trainer's own Profile (Screens/secure/Profile).
// Deliberately data-driven (SETTINGS_SECTIONS below) instead of hardcoded markup per section,
// so a future section (notifications, scheduling preferences, ...) is one more array entry, not
// a redesign. Two item `type`s for now:
//   - "nav"   — a clickable row that navigates to an existing screen (Termini).
//   - "theme" — the theme toggle, rendered inline (see ThemeRow below).
const SETTINGS_SECTIONS = [
  {
    key: "scheduling",
    title: "Raspored",
    items: [
      {
        key: "appointments",
        type: "nav",
        label: "Termini",
        description: "Radno vreme i dostupni termini",
        path: "/settings/appointments",
        icon: ScheduleOutlinedIcon,
      },
    ],
  },
  {
    key: "appearance",
    title: "Izgled",
    items: [{ key: "theme", type: "theme", label: "Tema" }],
  },
];

// Reuses the exact toggle-switch look already defined in AppShell.css
// (.hf-shell-theme-toggle-label / .hf-shell-toggle-switch / .hf-shell-toggle-knob) — those
// classes are global (AppShell's own CSS import), so this is the same visual control moved
// here, not a second implementation. `getStoredTheme`/`setStoredTheme` are the same
// theme/themeStorage.js functions AppShell used to call directly. Same icon | text | action
// row shape as every other Settings row (.hf-stg-row), with the switch as the trailing action.
const ThemeRow = () => {
  const [theme, setTheme] = useState(() => getStoredTheme());
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setStoredTheme(next);
  };

  return (
    <div
      className="hf-stg-row"
      onClick={toggle}
      role="switch"
      aria-checked={theme === "dark"}
    >
      {/* Icon matches the state label below it (Tamna → moon, Svetla → sun) rather than the
          switch target — the two used to be inverted relative to each other. */}
      <span className="hf-stg-row-icon">
        {theme === "dark" ? (
          <DarkModeOutlinedIcon />
        ) : (
          <LightModeOutlinedIcon />
        )}
      </span>
      <span className="hf-shell-theme-toggle-label hf-stg-row-text">
        <span className="hf-stg-row-label">Tema</span>
        <span className="hf-stg-row-desc">
          {theme === "dark" ? "Tamna" : "Svetla"}
        </span>
      </span>
      <span className="hf-stg-row-action">
        <span className={`hf-shell-toggle-switch${theme === "dark" ? " on" : ""}`}>
          <span className="hf-shell-toggle-knob" />
        </span>
      </span>
    </div>
  );
};

const Settings = () => {
  const navigate = useNavigate();

  return (
    <AppShell active="settings">
      <MobileHeader title="Podešavanja" />

      <div className="hf-stg-hub-page">
        <div className="hf-stg-hub-header">
          <h1 className="hf-stg-hub-title">Podešavanja</h1>
          <p className="hf-stg-hub-subtitle">Podešavanja aplikacije</p>
        </div>

        {SETTINGS_SECTIONS.map((section) => (
          <div className="hf-stg-section" key={section.key}>
            <h2 className="hf-stg-section-title">{section.title}</h2>
            <div className="hf-stg-section-card">
              {section.items.map((item) =>
                item.type === "theme" ? (
                  <ThemeRow key={item.key} />
                ) : (
                  <button
                    type="button"
                    key={item.key}
                    className="hf-stg-row"
                    onClick={() => navigate(item.path)}
                  >
                    <span className="hf-stg-row-icon">
                      <item.icon />
                    </span>
                    <span className="hf-stg-row-text">
                      <span className="hf-stg-row-label">{item.label}</span>
                      {item.description && (
                        <span className="hf-stg-row-desc">{item.description}</span>
                      )}
                    </span>
                    <span className="hf-stg-row-action">
                      <ChevronRightIcon className="hf-stg-row-chevron" />
                    </span>
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
};

export default Settings;
